<script setup lang="ts">
import type { Filters, Property } from '~/types/properties';

const {
  properties = [],
  filters,
  loading = false,
  isExpanded = false,
  zoom = 11,
  highlightedId = null,
  cardLinkMode = false,
  selectedId = null,
} = defineProps<{
  properties?: Property[];
  filters: Filters;
  loading?: boolean;
  isExpanded?: boolean;
  zoom?: number;
  /** complex_id of the hovered project card — its pin is emphasised. */
  highlightedId?: string | null;
  /** Marker click emits selectProperty instead of opening the popover. */
  cardLinkMode?: boolean;
  /** complex_id of the selected marker — emits select-position to anchor a card. */
  selectedId?: string | null;
}>();

const emit = defineEmits<{
  (
    e: 'updatePosition',
    payload: { topLeftLat: number; topLeftLng: number; bottomRightLat: number; bottomRightLng: number; zoom: number },
  ): void;
  (e: 'updateFilters', value: Filters): void;
  (e: 'toggleExpand'): void;
  (e: 'selectProperty', complexId: string): void;
  (e: 'selectPosition', pos: { x: number; y: number } | null): void;
}>();

const { t } = useI18n();

const mapToggleLabel = computed(() =>
  isExpanded ? t('explore.mapColumn.collapseMap') : t('explore.mapColumn.expandMap'),
);

const mapRef = ref<any>(null);

const onUpdatedPosition = (payload: {
  topLeftLat: number;
  topLeftLng: number;
  bottomRightLat: number;
  bottomRightLng: number;
  zoom: number;
}) => {
  emit('updatePosition', payload);
};

const onUpdateFilters = (value: Filters) => {
  emit('updateFilters', value);
};

defineExpose({
  resetButtons: () => mapRef.value?.resetButtons?.(),
});
</script>

<template>
  <div class="relative h-full w-full overflow-hidden rounded-2xl border border-black/[0.08] bg-[#EFEAD9]">
    <PropertiesMap
      ref="mapRef"
      :filters="filters"
      :properties="properties"
      :loading="loading"
      show-property-popover
      :highlighted-id="highlightedId"
      :card-link-mode="cardLinkMode"
      :selected-id="selectedId"
      :zoom="zoom"
      @updated:position="onUpdatedPosition"
      @update:filters="onUpdateFilters"
      @select-property="emit('selectProperty', $event)"
      @select-position="emit('selectPosition', $event)"
    />

    <!-- Expand / collapse desktop toggle. Physical `right-3` on purpose: the map canvas is pinned
         `dir="ltr"` (see PropertiesMap), so its zoom/locate controls always sit top-LEFT — a logical
         `end-3` would move this button onto them under RTL. rtl-codemod-ignore: canvas pinned LTR -->
    <button
      type="button"
      class="explore-map-btn absolute right-3 top-3 z-30 inline-flex h-9 items-center gap-1.5 rounded-xl border border-black/[0.08] bg-white px-3 text-[12px] font-semibold text-black-80 shadow-base transition-colors hover:bg-black-03"
      :aria-label="mapToggleLabel"
      @click="emit('toggleExpand')"
    >
      <UIIcon
        :name="isExpanded ? 'chevron-big-right' : 'chevron-big-left'"
        class="!size-4"
      />
      <span class="hidden tablet:inline">{{ mapToggleLabel }}</span>
    </button>
  </div>
</template>

<style scoped>
.explore-map-btn {
  box-shadow:
    0 2px 4px rgba(15, 21, 29, 0.2),
    0 1px 10px rgba(0, 0, 0, 0.12);
}
</style>
